import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { fmtError, fmtEUR, fmtDate } from "@/lib/api";
import { Calendar, Heart, Wallet, History, Sparkles, ArrowRight } from "lucide-react";

const ACCENTS = {
  lilac: "from-[#F5F0FF] to-white",
  peach: "from-[#FFF5F5] to-white",
  mint: "from-[#F0FDF4] to-white",
};

export default function ClientHomePage() {
  const { user } = useAuth();
  const { slug, api, info } = useTenant();
  const firstName = (user?.name || "").split(" ")[0] || "Bentornata";
  const [next, setNext] = useState(null);
  const [fidelity, setFidelity] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!api) return;
    Promise.all([api.myAppointments(), api.myFidelity(), api.myWallet()])
      .then(([appts, fid, w]) => {
        const upcoming = appts.filter((a) => a.status === "booked" && new Date(a.starts_at) >= new Date())
          .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))[0] || null;
        setNext(upcoming);
        setFidelity(fid);
        setWallet(w);
      })
      .catch((e) => setError(fmtError(e)));
  }, [api]);

  return (
    <div data-testid="client-welcome" className="max-w-3xl">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Area cliente</div>
      <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
        Ciao <span className="italic">{firstName}</span> <Sparkles size={28} className="inline -mt-2 text-[#A78BFA]" />
      </h1>
      <p className="mt-5 text-lg text-[#4A4A4A] font-light">
        Prenota un appuntamento, guarda i punti fidelity, controlla il wallet e rivivi i tuoi trattamenti.
      </p>

      {error && <div className="mt-4 text-sm text-[#B91C1C]">{error}</div>}

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <HomeCard
          to={next ? `/${slug}/app/appointments` : `/${slug}/app/book`}
          icon={Calendar}
          title="Prossimo appuntamento"
          body={next ? `${next.service_name} · ${fmtDate(next.starts_at, info?.timezone)}` : "Nessuno in programma. Prenotane uno ✨"}
          accent="lilac"
          cta={next ? "Vedi tutti" : "Prenota ora"}
          testId="card-next"
        />
        <HomeCard
          to={`/${slug}/app/fidelity`}
          icon={Heart}
          title="Punti fidelity"
          body={fidelity ? `${fidelity.points} punti · livello ${fidelity.level}` : "—"}
          accent="peach"
          cta="Vedi card"
          testId="card-fidelity"
        />
        <HomeCard
          to={`/${slug}/app/wallet`}
          icon={Wallet}
          title="Saldo wallet"
          body={wallet ? fmtEUR(wallet.balance) : "—"}
          accent="mint"
          cta="Apri wallet"
          testId="card-wallet"
        />
      </div>

      <div className="mt-10">
        <Link
          to={`/${slug}/app/history`}
          data-testid="link-history"
          className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#737373] underline underline-offset-4"
        >
          <History size={14} /> Cronologia trattamenti
        </Link>
      </div>
    </div>
  );
}

const HomeCard = ({ to, icon: Icon, title, body, accent, cta, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`relative rounded-3xl border border-black/5 p-7 bg-gradient-to-br ${ACCENTS[accent]} tilt-card group block`}
  >
    <div className="h-11 w-11 rounded-2xl bg-white shadow-sm border border-black/5 flex items-center justify-center">
      <Icon size={20} className="text-[#1A1A1A]" />
    </div>
    <h3 className="mt-5 font-serif tracking-tight text-2xl text-[#1A1A1A]">{title}</h3>
    <p className="mt-1.5 text-sm text-[#4A4A4A] font-light">{body}</p>
    <div className="mt-6 inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[#1A1A1A]">
      {cta} <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
    </div>
  </Link>
);
